/**
 * FlowConfigPanel — shared configuration panel for flow builders.
 *
 * Renders date range, event filter mode, event selection, start-from-event,
 * collapse repeats toggle, KPI event, and filters.
 *
 * Parameterized by FlowSectionConfig so the same component works for
 * Event Flow, Screen Flow, etc.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/ui/Select';
import { MultiSelect } from '@/sections/product/MultiSelect';
import { FilterBuilder } from '@/components/filters/FilterBuilder';
import { DatePresetPicker } from '@/components/filters/DatePresetPicker';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { cn } from '@/lib/cn';
import type { FlowState, FlowSectionConfig } from './flow.types';
import type { DateRange } from '@/lib/dates';

interface FlowConfigPanelProps {
  state: FlowState;
  onChange: (patch: Partial<FlowState>) => void;
  dates: DateRange;
  config: FlowSectionConfig;
}

export function FlowConfigPanel({ state, onChange, dates, config }: FlowConfigPanelProps) {
  /* ── Fetch available events ────────────────────────────────────────────── */
  const { data: eventsData, isLoading: eventsLoading } = useQuery<{ events: string[] }>({
    queryKey: ['flow-events', config.source, dates.startDate, dates.endDate],
    queryFn: () =>
      api.post<{ events: string[] }>(endpoints.flow.events(), {
        source: config.source,
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    enabled: Boolean(dates.startDate && dates.endDate),
    staleTime: 60_000,
  });

  const eventOptions = useMemo(() => {
    const events = eventsData?.events ?? [];
    return events.map((e) => ({ value: e, label: e }));
  }, [eventsData]);

  /* ── KPI options: regular events + special "(none)" ────────────────────── */
  const kpiOptions = useMemo(() => {
    const base = [{ value: '', label: 'None' }, { value: '(none)', label: '(none) — Flow ends' }];
    return [...base, ...eventOptions];
  }, [eventOptions]);

  /* ── Start-from-event options ──────────────────────────────────────────── */
  const startFromOptions = useMemo(() => {
    return [{ value: '', label: 'None (show all flows)' }, ...eventOptions];
  }, [eventOptions]);

  return (
    <div className="px-5 py-4 space-y-4">
      {/* ── Row 1: Date Range ────────────────────────────────────────────── */}
      <div className="grid grid-cols-[1fr] gap-3">
        <DatePresetPicker
          value={state.preset}
          onChange={(preset) => onChange({ preset })}
          startDate={state.start}
          endDate={state.end}
          onDateChange={(start, end) => onChange({ start, end, preset: '' })}
        />
      </div>

      {/* ── Row 2: Filter Mode + Event Selection ─────────────────────────── */}
      <div className="grid grid-cols-[180px_1fr] gap-3 items-end">
        <Select
          label={`${config.itemLabel} Filter Mode`}
          options={[
            { value: 'exclude', label: `Exclude ${config.itemsLabel}` },
            { value: 'include', label: `Include ${config.itemsLabel}` },
          ]}
          value={state.filterMode}
          onChange={(e) => onChange({ filterMode: e.target.value as 'include' | 'exclude' })}
        />

        <MultiSelect
          label={
            state.filterMode === 'include'
              ? `${config.itemsLabel} to Include`
              : `${config.itemsLabel} to Exclude`
          }
          options={eventOptions}
          value={state.events}
          onChange={(events) => onChange({ events })}
          placeholder={
            eventsLoading
              ? `Loading ${config.itemsLabel.toLowerCase()}...`
              : `Select ${config.itemsLabel.toLowerCase()}...`
          }
          disabled={eventsLoading}
        />
      </div>

      {/* ── Row 3: Start From Event + Collapse Repeats ───────────────────── */}
      <div className="grid grid-cols-[1fr_1fr] gap-3 items-end">
        <Select
          label={`Start From ${config.itemLabel}`}
          options={startFromOptions}
          value={state.startFrom}
          onChange={(e) => onChange({ startFrom: e.target.value })}
        />

        <div className="flex items-center gap-3 pb-0.5">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={state.collapse}
              onChange={(e) => onChange({ collapse: e.target.checked })}
              className="sr-only peer"
            />
            <div
              className={cn(
                'w-9 h-5 rounded-full transition-colors',
                'bg-slate-200 peer-checked:bg-brand-500',
                'after:content-[""] after:absolute after:top-0.5 after:left-[2px]',
                'after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform',
                'peer-checked:after:translate-x-full',
              )}
            />
            <span className="ml-2 text-sm text-slate-700">Collapse Repeats</span>
          </label>
        </div>
      </div>

      {/* ── Row 4: KPI Event ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-[1fr] gap-3 items-end">
        <Select
          label={`KPI ${config.itemLabel}`}
          options={kpiOptions}
          value={state.kpi}
          onChange={(e) => onChange({ kpi: e.target.value })}
        />
      </div>

      {/* ── Row 5: Filters ───────────────────────────────────────────────── */}
      <FilterBuilder
        value={state.filters}
        onChange={(filters) => onChange({ filters })}
      />
    </div>
  );
}
